// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useGenerateInvoices } from '@/billing/hooks/use-sweep-charges';
import { Link } from '@/components/link';
import {
  DescriptionOutlined,
  RadioButtonUncheckedOutlined,
} from '@/constants/hardfin-standard-icons';
import { useToast } from '@/contexts/toast-context';
import { pluralize } from '@/utilities/pluralize';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';

interface GenerateInvoicesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  billableCount: number;
}

export function GenerateInvoicesDialog(props: GenerateInvoicesDialogProps) {
  const { isOpen, onClose, billableCount } = props;

  // Freeze the count that's displayed in the dialog from the moment Generate
  // is clicked until the close animation fully completes. Without this, the
  // mutation invalidates the charges cache, billableCount drops to 0, and the
  // dialog body swaps to the "No billable charges" empty state mid-exit —
  // visible as a flicker before the dialog finishes closing.
  const [frozenCount, setFrozenCount] = useState<number | null>(null);
  const displayCount = frozenCount ?? billableCount;
  const { showToast } = useToast();

  const generateMutation = useGenerateInvoices({
    onSuccess: (result) => {
      const invoiceCount = result.invoicesCreated + result.invoicesUpdated;
      showToast({
        element: (
          <Typography>
            {pluralize(invoiceCount, { singular: 'invoice' })} generated{' '}
            <Link
              to="/billing/invoices"
              color="inherit"
              sx={{ fontWeight: 'bold', textDecoration: 'underline' }}
            >
              View invoices
            </Link>
          </Typography>
        ),
        severity: 'success',
      });
      onClose();
    },
  });

  const noBillableCharges = displayCount === 0;

  const handleGenerate = () => {
    setFrozenCount(billableCount);
    generateMutation.mutate();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={isOpen}
      onClose={onClose}
      TransitionProps={{ onExited: () => setFrozenCount(null) }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>Generate invoices</DialogTitle>

      <DialogContent>
        {noBillableCharges ? (
          <Stack alignItems="center" spacing={1} sx={{ py: 6 }}>
            <Typography variant="body1" fontWeight={500}>
              No billable charges found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              There are no charges marked as billable to generate invoices from
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={2.5} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Review the invoice generation preview below
            </Typography>
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Stack spacing={2.5}>
                <Stack direction="row" alignItems="flex-start" spacing={2}>
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      backgroundColor: 'primary.50',
                      flexShrink: 0,
                    }}
                  >
                    <RadioButtonUncheckedOutlined
                      sx={{ fontSize: 18, color: 'primary.main' }}
                    />
                  </Stack>
                  <Typography variant="body1" sx={{ pt: 0.5 }}>
                    <strong>
                      {pluralize(displayCount, {
                        singular: 'billable charge',
                      })}
                    </strong>{' '}
                    will move to invoices
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="flex-start" spacing={2}>
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      backgroundColor: 'success.50',
                      flexShrink: 0,
                    }}
                  >
                    <DescriptionOutlined
                      sx={{ fontSize: 18, color: 'success.main' }}
                    />
                  </Stack>
                  <Stack spacing={0.75} sx={{ pt: 0.5 }}>
                    <Typography variant="body1">
                      Charges will be grouped by project
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      If a draft invoice already exists for the project, charges
                      will be added to the invoice
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      If a draft invoice does not exist for the project, a new
                      invoice will be created for the charges
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Paper>
            <Typography variant="body2" color="text.secondary">
              Draft invoices can be reviewed and edited before they are
              finalized
            </Typography>
          </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}
      >
        {noBillableCharges ? (
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={generateMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={generateMutation.isPending}
            >
              {generateMutation.isPending ? (
                <CircularProgress size={20} sx={{ mr: 1 }} />
              ) : null}
              Generate invoices
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
